import {reportTraceData} from '@github-ui/internal-api-insights'
import type {createSafeIndexedDbPersister} from '@github-ui/safe-indexed-db-storage'
import type {Sink} from 'relay-runtime/lib/network/RelayObservable'

type Storage = ReturnType<typeof createSafeIndexedDbPersister>

interface StoragePersisterOptions {
  /** The storage client used for setting and retrieving items from cache.
   * For SSR pass in `undefined`.
   */
  storage: Storage | undefined | null
  /**
   * The max-allowed age of the cache in milliseconds.
   * If a persisted cache is found that is older than this
   * time, it will be discarded
   */
  maxAgeMs: number
  /**
   * Prefix to be used for storage key.
   * Storage key is a combination of prefix and query hash in a form of `prefix-queryHash`.
   */
  prefix: string
}
type CacheKey = string

/**
 * Generates a cache key from query ID and variables
 */
function generateCacheKey(
  prefix: string,
  queryId: string,
  variables: Record<string, unknown>,
  defaultValues?: Record<string, unknown>,
): CacheKey {
  const variablesWithDefaults = {...defaultValues, ...variables}
  const sortedVariableKeys = Object.keys(variablesWithDefaults).sort()
  const sortedVariables: Record<string, unknown> = {}
  for (const key of sortedVariableKeys) {
    const value = variablesWithDefaults[key]
    if (value !== undefined && value !== null) {
      sortedVariables[key] = value
    }
  }
  return `${prefix}-${queryId}#${JSON.stringify(sortedVariables)}`
}

export type QueryCache<T> = ReturnType<typeof createQueryCache<T>>

export function createQueryCache<T>({storage, prefix, maxAgeMs}: StoragePersisterOptions) {
  async function retrieveQuery(
    queryId: string,
    variables: Record<string, unknown>,
    attributes?: {[key: string]: unknown},
    defaultValues?: Record<string, unknown>,
  ): Promise<T | undefined> {
    if (!storage) {
      return undefined
    }
    const cacheKey = generateCacheKey(prefix, queryId, variables, defaultValues)
    try {
      const persistedQuery = await storage.getItem(cacheKey, attributes)
      return persistedQuery?.state.data as T | undefined
    } catch {
      return undefined
    }
  }

  async function tryCacheFetchResult(
    queryId: string,
    variables: Record<string, unknown>,
    fetchResult: T | null,
    ttl: number,
    attributes?: {[key: string]: unknown},
    defaultValues?: Record<string, unknown>,
  ) {
    if (!storage) {
      return undefined
    }
    const cacheKey = generateCacheKey(prefix, queryId, variables, defaultValues)
    try {
      await storage.setItem(
        cacheKey,
        {
          queryHash: queryId,
          queryKey: Object.entries(variables).flat(),
          state: {data: fetchResult, dataUpdatedAt: Date.now(), ttl},
        },
        undefined,
        undefined,
        attributes,
      )
    } catch {
      // eslint-disable-next-line no-console
      console.warn(`Failed to persist query for key: ${cacheKey}`)
    }
  }

  async function persisterFn(
    queryFn: (isCacheRefresh?: boolean) => Promise<T>,
    queryId: string,
    variables: Record<string, unknown>,
    observer: Sink<T>,
    ttl?: number,
    attributes?: {[key: string]: unknown},
    defaultValues?: Record<string, unknown>,
  ): Promise<T> {
    const cachedResult = await retrieveQuery(queryId, variables, attributes, defaultValues)

    let cachedResultString: string | null = null
    let queryResultString: string | null = null
    if (cachedResult) {
      if (typeof cachedResult === 'object' && cachedResult !== null && 'data' in cachedResult) {
        cachedResultString = JSON.stringify((cachedResult as {[key: string]: unknown}).data)
      }
      reportTraceData({
        __trace: {
          query_name: attributes?.query_name as string | undefined,
          query_id: queryId,
          query_variables: variables,
          duration_ms: 1,
          cache_result: 'hit',
        },
      })
      observer.next(cachedResult)
    }

    const fetchResult = await queryFn(!!cachedResult)

    if (fetchResult && storage) {
      if (typeof fetchResult === 'object' && fetchResult !== null && 'data' in fetchResult) {
        queryResultString = JSON.stringify((fetchResult as {[key: string]: unknown}).data)
      }
      const updateType = !cachedResult ? 'new' : cachedResultString !== queryResultString ? 'changed' : 'unchanged'
      tryCacheFetchResult(
        queryId,
        variables,
        fetchResult,
        ttl ?? maxAgeMs,
        {...attributes, update_type: updateType},
        defaultValues,
      )
    }

    return fetchResult
  }

  return {persisterFn, retrieveQuery}
}
